import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Stack,
    Alert,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useStore } from '../../stores';
import { Vehicle, VehicleFormData } from '../../stores/VehicleStore';

interface VehicleEditFormProps {
    vehicle: Vehicle;
    onSuccess?: () => void;
    onCancel?: () => void;
}

const VehicleEditForm: React.FC<VehicleEditFormProps> = observer(({ vehicle, onSuccess, onCancel }) => {
    const { vehicleStore } = useStore();
    const [formData, setFormData] = useState<VehicleFormData>({
        license_plate: '',
        brand: '',
        model: '',
        year: '',
        color: '',
        vin: '',
        engine_number: '',
        acquisition_date: '',
        airtag_number: '',
        status: 'ACTIVE',
        image_url: ''
    });

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (vehicle) {
            setFormData({
                license_plate: vehicle.license_plate || '',
                brand: vehicle.brand || '',
                model: vehicle.model || '',
                year: vehicle.year?.toString() || '',
                color: vehicle.color || '',
                vin: vehicle.vin || '',
                engine_number: vehicle.engine_number || '',
                acquisition_date: vehicle.acquisition_date || '',
                airtag_number: vehicle.airtag_number || '',
                status: vehicle.status || 'ACTIVE',
                image_url: vehicle.image_url || ''
            });
        }
    }, [vehicle]);

    const handleInputChange = (field: keyof VehicleFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: '' }));
        }
        if (vehicleStore.error) {
            vehicleStore.clearError();
        }
    };

    const handleSelectChange = (field: keyof VehicleFormData) => (event: any) => {
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSuccessMessage('');

        const validation = vehicleStore.validateVehicleForm(formData);

        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            return;
        }

        const success = await vehicleStore.updateVehicle(vehicle.id, formData);

        if (success) {
            setValidationErrors({});
            setSuccessMessage('Vehículo actualizado exitosamente');

            if (onSuccess) {
                setTimeout(onSuccess, 1500);
            }
        }
    };

    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    return (
        <Box>
            <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Placa del Vehículo *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: ABC123"
                            value={formData.license_plate}
                            onChange={handleInputChange('license_plate')}
                            error={!!validationErrors.license_plate}
                            helperText={validationErrors.license_plate}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 10 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Marca *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: Toyota, Honda, Yamaha"
                            value={formData.brand}
                            onChange={handleInputChange('brand')}
                            error={!!validationErrors.brand}
                            helperText={validationErrors.brand}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Modelo *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: Corolla, CBR600RR"
                            value={formData.model}
                            onChange={handleInputChange('model')}
                            error={!!validationErrors.model}
                            helperText={validationErrors.model}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Año *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: 2023"
                            value={formData.year}
                            onChange={handleInputChange('year')}
                            error={!!validationErrors.year}
                            helperText={validationErrors.year}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            type="number"
                            inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Color *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: Blanco, Negro, Rojo"
                            value={formData.color}
                            onChange={handleInputChange('color')}
                            error={!!validationErrors.color}
                            helperText={validationErrors.color}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 30 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Serial del vehículo *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: 1HGBH41JXMN109186"
                            value={formData.vin}
                            onChange={handleInputChange('vin')}
                            error={!!validationErrors.vin}
                            helperText={validationErrors.vin || 'Código único de 17 caracteres que identifica el vehículo'}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Serial del Motor *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: ENG123456789"
                            value={formData.engine_number}
                            onChange={handleInputChange('engine_number')}
                            error={!!validationErrors.engine_number}
                            helperText={validationErrors.engine_number}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Fecha de Adquisición *
                        </Typography>
                        <TextField
                            fullWidth
                            value={formData.acquisition_date}
                            onChange={handleInputChange('acquisition_date')}
                            error={!!validationErrors.acquisition_date}
                            helperText={validationErrors.acquisition_date}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            type="date"
                            inputProps={{ max: getTodayDate() }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Número de AirTag *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="ej: AIRTAG123456789"
                            value={formData.airtag_number}
                            onChange={handleInputChange('airtag_number')}
                            error={!!validationErrors.airtag_number}
                            helperText={validationErrors.airtag_number || 'Código único del AirTag para rastreo del vehículo'}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            inputProps={{ maxLength: 50 }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            Estado
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                value={formData.status}
                                onChange={handleSelectChange('status')}
                                disabled={vehicleStore.isSubmitting}
                                IconComponent={KeyboardArrowDownIcon}
                                sx={{
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                    '& .MuiSelect-select': {
                                        p: 1.5,
                                    }
                                }}
                            >
                                <MenuItem value="ACTIVE">Activo</MenuItem>
                                <MenuItem value="MAINTENANCE">En Mantenimiento</MenuItem>
                                <MenuItem value="INACTIVE">Inactivo</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="500" mb={1}>
                            URL de Imagen (Opcional)
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Por ajustar"
                            value={formData.image_url}
                            onChange={handleInputChange('image_url')}
                            error={!!validationErrors.image_url}
                            helperText={validationErrors.image_url}
                            disabled={vehicleStore.isSubmitting}
                            variant="outlined"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: 'white',
                                }
                            }}
                        />
                    </Box>

                    {successMessage && (
                        <Alert
                            severity="success"
                            icon={<CheckCircleIcon />}
                            sx={{ mb: 3 }}
                        >
                            {successMessage}
                        </Alert>
                    )}

                    {vehicleStore.error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {vehicleStore.error}
                        </Alert>
                    )}

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        {onCancel && (
                            <Button
                                variant="outlined"
                                onClick={onCancel}
                                disabled={vehicleStore.isSubmitting}
                                sx={{
                                    borderRadius: 1.5,
                                    px: 3,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                }}
                            >
                                Cancelar
                            </Button>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={vehicleStore.isSubmitting}
                            sx={{
                                backgroundColor: '#1A1A1A',
                                color: 'white',
                                borderRadius: 1.5,
                                px: 3,
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 500,
                                '&:hover': {
                                    backgroundColor: '#333333',
                                },
                                '&:disabled': {
                                    backgroundColor: '#cccccc',
                                },
                            }}
                        >
                            {vehicleStore.isSubmitting ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                    Actualizando...
                                </>
                            ) : (
                                'Actualizar Vehículo'
                            )}
                        </Button>
                    </Stack>
                </Stack>
            </form>
        </Box>
    );
});

export default VehicleEditForm;
